import { logger } from "@/lib/logger";
/**
 * Offline File Queue using IndexedDB
 * Stores files locally when offline for later upload
 * 
 * CRASH-SAFE: All methods handle IndexedDB unavailability gracefully
 */

import { isIndexedDBAvailable, safeDispatchEvent, hasWindow } from '@/lib/browserSafeUtils';

interface QueuedFileItem {
  id: string;
  file: Blob;
  fileName: string;
  fileType: string;
  fileSize: number;
  patientId: string;
  trialSiteId: string;
  documentType: string;
  timestamp: number;
  status: 'pending' | 'processing' | 'failed';
  retryCount: number;
  errorMessage?: string;
}

const DB_NAME = 'pharma-trial-offline-files';
const STORE_NAME = 'file-queue';
const DB_VERSION = 1;

class OfflineFileQueue {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private initFailed: boolean = false;

  async init(): Promise<void> {
    // If init already failed, don't retry
    if (this.initFailed) {
      return;
    }

    // If already initialized, return
    if (this.db) {
      return;
    }

    // If init is in progress, wait for it
    if (this.initPromise) {
      return this.initPromise;
    }

    // Check if IndexedDB is available
    if (!isIndexedDBAvailable()) {
      logger.warn('[offlineFileQueue] IndexedDB not available in this environment');
      this.initFailed = true;
      return;
    }

    this.initPromise = new Promise<void>((resolve) => {
      try {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
          logger.warn('[offlineFileQueue] Failed to open IndexedDB:', request.error);
          this.initFailed = true;
          this.initPromise = null;
          resolve(); // Resolve, don't reject - we want graceful degradation
        };

        request.onsuccess = () => {
          this.db = request.result;
          this.initPromise = null;
          resolve();
        };

        request.onupgradeneeded = (event) => {
          try {
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
              db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            }
          } catch (error) {
            logger.warn('[offlineFileQueue] Failed to upgrade database:', error);
          }
        };

        // Handle blocked event (when another tab has the DB open)
        request.onblocked = () => {
          logger.warn('[offlineFileQueue] Database blocked by another connection');
          this.initFailed = true;
          this.initPromise = null;
          resolve();
        };
      } catch (error) {
        logger.warn('[offlineFileQueue] Exception during IndexedDB init:', error);
        this.initFailed = true;
        this.initPromise = null;
        resolve();
      }
    });

    return this.initPromise;
  }

  async addFile(
    file: File,
    patientId: string,
    trialSiteId: string,
    documentType: string
  ): Promise<string> {
    try {
      await this.init();
      
      if (!this.db) {
        logger.warn('[offlineFileQueue] Cannot add file - IndexedDB not available');
        return '';
      }

      const id = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const item: QueuedFileItem = {
        id,
        file,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        patientId,
        trialSiteId,
        documentType,
        timestamp: Date.now(),
        status: 'pending',
        retryCount: 0,
      };

      return new Promise((resolve) => {
        try {
          const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
          const store = transaction.objectStore(STORE_NAME);
          const request = store.add(item);

          request.onsuccess = () => {
            logger.debug('File queued for offline upload:', id);
            this.dispatchUpdateEvent();
            resolve(id);
          };
          request.onerror = () => {
            logger.warn('[offlineFileQueue] Failed to add file:', request.error);
            resolve('');
          };
        } catch (error) {
          logger.warn('[offlineFileQueue] Exception adding file:', error);
          resolve('');
        }
      });
    } catch (error) {
      logger.warn('[offlineFileQueue] addFile error:', error);
      return '';
    }
  }

  async getAllItems(): Promise<QueuedFileItem[]> {
    try {
      await this.init();
      
      if (!this.db) {
        return [];
      }

      return new Promise((resolve) => {
        try {
          const transaction = this.db!.transaction([STORE_NAME], 'readonly');
          const store = transaction.objectStore(STORE_NAME);
          const request = store.getAll();

          request.onsuccess = () => resolve(request.result || []);
          request.onerror = () => {
            logger.warn('[offlineFileQueue] Failed to get items:', request.error);
            resolve([]);
          };
        } catch (error) {
          logger.warn('[offlineFileQueue] Exception getting items:', error);
          resolve([]);
        }
      });
    } catch (error) {
      logger.warn('[offlineFileQueue] getAllItems error:', error);
      return [];
    }
  }

  async updateItem(id: string, updates: Partial<QueuedFileItem>): Promise<void> {
    try {
      await this.init();
      
      if (!this.db) {
        return;
      }

      return new Promise((resolve) => {
        try {
          const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
          const store = transaction.objectStore(STORE_NAME);
          
          const getRequest = store.get(id);
          getRequest.onsuccess = () => {
            const item = getRequest.result;
            if (!item) {
              resolve();
              return;
            }

            const updatedItem = { ...item, ...updates };
            const putRequest = store.put(updatedItem);
            
            putRequest.onsuccess = () => {
              this.dispatchUpdateEvent();
              resolve();
            };
            putRequest.onerror = () => {
              logger.warn('[offlineFileQueue] Failed to update item:', putRequest.error);
              resolve();
            };
          };
          getRequest.onerror = () => {
            logger.warn('[offlineFileQueue] Failed to get item for update:', getRequest.error);
            resolve();
          };
        } catch (error) {
          logger.warn('[offlineFileQueue] Exception updating item:', error);
          resolve();
        }
      });
    } catch (error) {
      logger.warn('[offlineFileQueue] updateItem error:', error);
    }
  }

  async removeItem(id: string): Promise<void> {
    try {
      await this.init();
      
      if (!this.db) {
        return;
      }

      return new Promise((resolve) => {
        try {
          const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
          const store = transaction.objectStore(STORE_NAME);
          const request = store.delete(id);

          request.onsuccess = () => {
            logger.debug('File removed from queue:', id);
            this.dispatchUpdateEvent();
            resolve();
          };
          request.onerror = () => {
            logger.warn('[offlineFileQueue] Failed to remove item:', request.error);
            resolve();
          };
        } catch (error) {
          logger.warn('[offlineFileQueue] Exception removing item:', error);
          resolve();
        }
      });
    } catch (error) {
      logger.warn('[offlineFileQueue] removeItem error:', error);
    }
  }

  async getQueueCount(): Promise<number> {
    try {
      await this.init();
      
      if (!this.db) {
        return 0;
      }

      return new Promise((resolve) => {
        try {
          const transaction = this.db!.transaction([STORE_NAME], 'readonly');
          const store = transaction.objectStore(STORE_NAME);
          const request = store.count();

          request.onsuccess = () => resolve(request.result || 0);
          request.onerror = () => {
            logger.warn('[offlineFileQueue] Failed to get count:', request.error);
            resolve(0);
          };
        } catch (error) {
          logger.warn('[offlineFileQueue] Exception getting count:', error);
          resolve(0);
        }
      });
    } catch (error) {
      logger.warn('[offlineFileQueue] getQueueCount error:', error);
      return 0;
    }
  }

  private dispatchUpdateEvent() {
    safeDispatchEvent('offline-file-queue-updated');
  }
}

// Singleton instance - created lazily to avoid constructor errors
let _offlineFileQueueInstance: OfflineFileQueue | null = null;

function getOfflineFileQueue(): OfflineFileQueue {
  if (!_offlineFileQueueInstance) {
    _offlineFileQueueInstance = new OfflineFileQueue();
  }
  return _offlineFileQueueInstance;
}

// Export a proxy that safely accesses the singleton
export const offlineFileQueue = {
  init: () => getOfflineFileQueue().init(),
  addFile: (file: File, patientId: string, trialSiteId: string, documentType: string) => 
    getOfflineFileQueue().addFile(file, patientId, trialSiteId, documentType),
  getAllItems: () => getOfflineFileQueue().getAllItems(),
  updateItem: (id: string, updates: Partial<QueuedFileItem>) => 
    getOfflineFileQueue().updateItem(id, updates),
  removeItem: (id: string) => getOfflineFileQueue().removeItem(id),
  getQueueCount: () => getOfflineFileQueue().getQueueCount(),
};